'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Download, Sun, Moon } from 'lucide-react';
import { useTheme } from '@/lib/ThemeContext';

const REFRESH_INTERVAL = 30;

export default function DashboardHeader() {
  const [now, setNow] = useState<Date | null>(null);
  const [countdown, setCountdown] = useState(REFRESH_INTERVAL);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const d = new Date();
    setNow(d);
    setLastUpdated(d);
  }, []);

  useEffect(() => {
    const tick = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) { setLastUpdated(new Date()); return REFRESH_INTERVAL; }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  const handleRefresh = useCallback(() => {
    setIsRefreshing(true);
    setCountdown(REFRESH_INTERVAL);
    setLastUpdated(new Date());
    setTimeout(() => setIsRefreshing(false), 800);
  }, []);

  const formatTime = (d: Date) =>
    d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });

  const formatDate = (d: Date) =>
    d.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  const progressPct = ((REFRESH_INTERVAL - countdown) / REFRESH_INTERVAL) * 100;

  return (
    <div className="flex items-start justify-between mb-6 flex-wrap gap-3">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <h1 className="text-2xl font-700 text-foreground tracking-tight">Operations Dashboard</h1>
          <span className="px-2 py-0.5 rounded-full text-[10px] font-700 uppercase tracking-wider bg-positive/15 text-positive border border-positive/20">
            Live
          </span>
        </div>
        <p className="text-sm text-muted-foreground">
          {now ? formatDate(now) : 'Loading...'} · All shifts active
        </p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {/* Live clock */}
        <div className="flex items-center gap-1.5 px-3 py-2 glass-card rounded-xl text-xs font-mono font-600 text-foreground tabular-nums">
          <span className="w-1.5 h-1.5 rounded-full bg-positive animate-pulse" />
          {now ? formatTime(now) : '--:--:--'}
        </div>

        {/* Auto-refresh */}
        <div className="flex items-center gap-2 px-3 py-2 glass-card rounded-xl text-xs font-500 text-muted-foreground">
          <span className="relative w-4 h-4 flex items-center justify-center">
            <svg className="w-4 h-4 -rotate-90" viewBox="0 0 16 16">
              <circle cx="8" cy="8" r="6" fill="none" stroke="currentColor" strokeOpacity="0.2" strokeWidth="2" />
              <circle
                cx="8" cy="8" r="6" fill="none" stroke="var(--positive)" strokeWidth="2"
                strokeDasharray={`${2 * Math.PI * 6}`}
                strokeDashoffset={`${2 * Math.PI * 6 * (1 - progressPct / 100)}`}
                strokeLinecap="round"
                style={{ transition: 'stroke-dashoffset 1s linear' }}
              />
            </svg>
          </span>
          <span className="tabular-nums text-positive font-600">{countdown}s</span>
        </div>

        <button
          onClick={handleRefresh}
          className="flex items-center gap-1.5 px-3 py-2 glass-card rounded-xl text-sm font-500 text-muted-foreground hover:text-foreground transition-all duration-200 hover:shadow-md"
        >
          <RefreshCw size={14} className={isRefreshing ? 'animate-spin' : ''} />
          <span className="hidden sm:inline">Refresh</span>
        </button>

        <button className="flex items-center gap-1.5 px-3 py-2 glass-card rounded-xl text-sm font-500 text-muted-foreground hover:text-foreground transition-all duration-200 hover:shadow-md">
          <Download size={14} />
          <span className="hidden sm:inline">Export</span>
        </button>

        <button
          onClick={toggleTheme}
          className="flex items-center justify-center w-9 h-9 glass-card rounded-xl text-muted-foreground hover:text-foreground transition-all duration-200 hover:shadow-md"
          aria-label="Toggle theme"
        >
          {theme === 'dark' ? <Sun size={15} className="text-accent" /> : <Moon size={15} />}
        </button>
      </div>
    </div>
  );
}